import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Star, FileImage, X, Loader2, ChevronUp, ChevronDown, Library,
} from 'lucide-react';
import { slideshowApi, SlideshowImage, SLIDE_DURATIONS } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatFileSize, useLibraryUpload } from '@/hooks/useLibraryUpload';
import { LibraryUploadPanel } from '@/components/slideshows/LibraryUploadPanel';
import { LibraryPickerDialog } from '@/components/slideshows/LibraryPickerDialog';

/** "1 min 36 s" — total time for one pass through the slideshow. */
const formatCycle = (secs: number) => {
  const m = Math.floor(secs / 60), s = secs % 60;
  return m === 0 ? `${s} s` : s === 0 ? `${m} min` : `${m} min ${s} s`;
};

export default function SlideshowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const slideshowId = Number(id);
  const queryClient = useQueryClient();

  const [pageError, setPageError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { data, isLoading } = useQuery({
    queryKey: ['slideshow', slideshowId],
    queryFn: () => slideshowApi.get(slideshowId),
  });

  const slideshow = data?.slideshow;
  const images: SlideshowImage[] = slideshow?.images ?? [];
  const cycleSeconds = images.reduce((sum, i) => sum + i.duration, 0);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['slideshow', slideshowId] });
    queryClient.invalidateQueries({ queryKey: ['slideshows'] });
    queryClient.invalidateQueries({ queryKey: ['slideshow-library'] });
  };

  // ── Mutations ────────────────────────────────────────────────────────────────

  const reorderMutation = useMutation({
    mutationFn: (ids: number[]) => slideshowApi.reorderImages(slideshowId, ids),
    onSuccess: invalidate,
    onError: (e: any) => setPageError(e.message),
  });

  const removeImageMutation = useMutation({
    mutationFn: (imageId: number) => slideshowApi.deleteImage(slideshowId, imageId),
    onSuccess: invalidate,
    onError: (e: any) => setPageError(e.message),
  });

  const durationMutation = useMutation({
    mutationFn: ({ imageId, duration }: { imageId: number; duration: number }) =>
      slideshowApi.updateImage(slideshowId, imageId, { duration }),
    onSuccess: invalidate,
    onError: (e: any) => setPageError(e.message),
  });

  const addImagesMutation = useMutation({
    mutationFn: (libraryImageIds: number[]) => slideshowApi.addImages(slideshowId, libraryImageIds),
    onSuccess: () => { invalidate(); setPickerOpen(false); },
    onError: (e: any) => setPageError(e.message),
  });

  const setDefaultMutation = useMutation({
    mutationFn: () => slideshowApi.setDefault(slideshowId),
    onSuccess: invalidate,
    onError: (e: any) => setPageError(e.message),
  });

  // Uploads land in the facility's library first, then join this slideshow.
  const upload = useLibraryUpload({
    facilityId: slideshow?.facilityId,
    maxFileSize: data?.maxFileSize ?? null,
    onUploaded: async (uploaded) => {
      await addImagesMutation.mutateAsync(uploaded.map(u => u.id));
    },
  });

  // ── Reorder helpers ───────────────────────────────────────────────────────────

  const moveImage = (idx: number, direction: -1 | 1) => {
    const ids = images.map(i => i.id);
    const swap = idx + direction;
    const a = ids[swap]!; const b = ids[idx]!;
    ids[swap] = b; ids[idx] = a;
    reorderMutation.mutate(ids);
  };

  // ── Loading / not-found ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!slideshow) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <p className="text-gray-500">Slideshow not found.</p>
        <Link to="/slideshows" className="mt-4 inline-block text-blue-600 hover:underline">← Back to Slideshows</Link>
      </div>
    );
  }

  const inSlideshow = new Set(images.map(i => i.libraryImageId).filter((x): x is number => typeof x === 'number'));

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <Link
          to="/slideshows"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <ArrowLeft className="h-4 w-4" />Back to Slideshows
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{slideshow.name}</h1>
              {slideshow.isDefault && (
                <Badge className="border-0 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                  Default
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add sponsor images from your Image Library, or upload new ones — PNG, JPG, or WebP
              {upload.limitLabel ? ` · up to ${upload.limitLabel} each` : ''}
            </p>
          </div>
          {!slideshow.isDefault && (
            <Button
              variant="outline"
              onClick={() => setDefaultMutation.mutate()}
              disabled={setDefaultMutation.isPending}
              className="border-yellow-300 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 dark:hover:bg-yellow-900/30"
            >
              <Star className="mr-2 h-4 w-4" />Set as Default
            </Button>
          )}
        </div>
      </div>

      {/* Page error */}
      {pageError && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <span>{pageError}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setPageError(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">

        {/* ── Left: add images ── */}
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Images</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Reuse an image from the library, or upload a new one</p>
          </div>
          <div className="space-y-6 p-6">
            <Button variant="outline" className="w-full" onClick={() => setPickerOpen(true)}>
              <Library className="mr-2 h-4 w-4" />Add from Image Library
            </Button>
            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
              <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />or upload new<span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>
            <LibraryUploadPanel upload={upload} uploadVerb="Upload & add" />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              New uploads are saved to the Image Library too, so other slideshows can use them.
            </p>
          </div>
        </div>

        {/* ── Right: Current images ── */}
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Current Images
              {images.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                  {images.length} image{images.length !== 1 ? 's' : ''} · {formatCycle(cycleSeconds)} per cycle
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {images.length === 0
                ? 'No images yet'
                : 'Images play in the order shown, each for its own duration'}
            </p>
          </div>
          <div className="p-6">
            {images.length === 0 ? (
              <div className="py-12 text-center">
                <FileImage className="mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-400 dark:text-gray-500">No images yet</p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Add one from the library or upload a sponsor image to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {images.map((img, idx) => (
                  <div
                    key={img.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    {/* Thumbnail */}
                    <div className="mr-3 h-10 w-20 flex-shrink-0 overflow-hidden rounded bg-gray-100 dark:bg-gray-700">
                      <img
                        src={img.signedUrl ?? img.s3Url}
                        alt={img.originalName}
                        className="h-full w-full object-contain"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200" title={img.originalName}>
                        {img.originalName}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatFileSize(img.fileSize)}
                        {img.width && img.height ? ` · ${img.width}×${img.height}` : ''}
                      </p>
                    </div>

                    {/* Duration */}
                    <label className="ml-2 flex flex-shrink-0 items-center gap-1 text-xs text-gray-500 dark:text-gray-400" title="How long this image stays on screen">
                      <select
                        value={img.duration}
                        onChange={e => durationMutation.mutate({ imageId: img.id, duration: Number(e.target.value) })}
                        disabled={durationMutation.isPending}
                        aria-label={`Duration for ${img.originalName}`}
                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      >
                        {/* An off-grid value can only predate 1.29.0's backfill; show it so the select isn't blank. */}
                        {!(SLIDE_DURATIONS as readonly number[]).includes(img.duration) && (
                          <option value={img.duration}>{img.duration} s</option>
                        )}
                        {SLIDE_DURATIONS.map(s => <option key={s} value={s}>{s} s</option>)}
                      </select>
                    </label>

                    {/* Reorder */}
                    <div className="ml-2 flex flex-shrink-0 flex-col gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-gray-300 hover:text-gray-600 dark:hover:text-gray-400"
                        disabled={idx === 0 || reorderMutation.isPending}
                        onClick={() => moveImage(idx, -1)}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-gray-300 hover:text-gray-600 dark:hover:text-gray-400"
                        disabled={idx === images.length - 1 || reorderMutation.isPending}
                        onClick={() => moveImage(idx, 1)}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Remove (file stays in the library) */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Remove from slideshow (stays in library)"
                      className="ml-1 h-8 w-8 text-gray-300 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                      onClick={() => {
                        if (!window.confirm(`Remove "${img.originalName}" from this slideshow? It stays in the Image Library.`)) return;
                        removeImageMutation.mutate(img.id);
                      }}
                      disabled={removeImageMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>

      {pickerOpen && (
        <LibraryPickerDialog
          facilityId={slideshow.facilityId}
          inSlideshow={inSlideshow}
          adding={addImagesMutation.isPending}
          onAdd={ids => addImagesMutation.mutate(ids)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
